import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  GripVertical, ArrowUp, ArrowDown, Loader2, Clock, RotateCcw, MapPin, AlertTriangle,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { formatDistance, formatDuration } from "../../utils/googleMaps";
import { API } from "../../App";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const StopRow = ({ stop, index, count, legFromPrev, color, onMove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: stop.job_id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-start gap-2 rounded-lg border border-border/60 bg-theme-surface px-2 py-2 ${isDragging ? "opacity-70 ring-1 ring-[#FFCC00]/60 z-10 relative" : ""}`}
      data-testid={`route-stop-${stop.job_id}`}
    >
      <button
        className="p-1 mt-0.5 text-muted-foreground cursor-grab touch-none flex-shrink-0"
        {...attributes}
        {...listeners}
        title="Drag to reorder"
        data-testid={`route-stop-drag-${stop.job_id}`}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span
        className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0 mt-0.5"
        style={{ background: color }}
        data-testid={`route-stop-number-${stop.job_id}`}
      >
        {index + 1}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">
          {stop.customer_name || "No name"}
          {stop.title ? <span className="text-muted-foreground font-normal"> — {stop.title}</span> : null}
        </p>
        {stop.customer_address && (
          <p className="text-[11px] text-muted-foreground truncate flex items-center gap-1">
            <MapPin className="w-3 h-3 flex-shrink-0" />{stop.customer_address}
          </p>
        )}
        <p className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {stop.no_time ? "No time set" : `${stop.scheduled_time}${stop.scheduled_time_end ? ` – ${stop.scheduled_time_end}` : ""}`}
          </span>
          {legFromPrev && (
            <span className="text-foreground/70" data-testid={`route-stop-leg-${stop.job_id}`}>
              {formatDistance(legFromPrev.distance_m)} · {formatDuration(legFromPrev.duration_s)} from previous
            </span>
          )}
        </p>
      </div>
      <div className="flex flex-col gap-0.5 flex-shrink-0">
        <button
          onClick={() => onMove(index, index - 1)}
          disabled={index === 0}
          className="p-1 rounded text-muted-foreground hover:text-foreground disabled:opacity-30"
          title="Move up"
          data-testid={`route-stop-up-${stop.job_id}`}
        >
          <ArrowUp className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => onMove(index, index + 1)}
          disabled={index === count - 1}
          className="p-1 rounded text-muted-foreground hover:text-foreground disabled:opacity-30"
          title="Move down"
          data-testid={`route-stop-down-${stop.job_id}`}
        >
          <ArrowDown className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// Route planner for the Jobs map — pick a technician, see their day's stops
// (addresses, scheduled windows, leg distances) and arrange the sequence by
// drag-and-drop or the up/down buttons. Order persists per tech + day,
// separate from appointment times.
const RoutePanel = ({ techs = [], techColorMap = {}, date, selectedTech, setSelectedTech, onRouteData, refreshKey }) => {
  const [stops, setStops] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [route, setRoute] = useState(null);
  const [orderSource, setOrderSource] = useState("time");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const firstLoadRef = useRef(true);

  const dateStr = format(date, "yyyy-MM-dd");
  const color = techColorMap[selectedTech] || "#FFCC00";

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  );

  const fetchRoute = useCallback(async (quiet = false) => {
    if (!selectedTech) {
      setStops([]); setUnmapped([]); setRoute(null);
      onRouteData?.(null);
      return;
    }
    if (!quiet) setLoading(true);
    try {
      const res = await axios.get(`${API}/team/map/tech-route/${selectedTech}?date=${dateStr}`, auth());
      setStops(res.data.stops || []);
      setUnmapped(res.data.unmapped || []);
      setRoute(res.data.route || null);
      setOrderSource(res.data.order_source || "time");
      onRouteData?.({ techId: selectedTech, stops: res.data.stops || [], route: res.data.route || null });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load route");
      onRouteData?.(null);
    } finally {
      setLoading(false);
    }
  }, [selectedTech, dateStr, onRouteData]);

  useEffect(() => {
    firstLoadRef.current = true;
    fetchRoute();
  }, [fetchRoute]);

  // Jobs/assignments changed elsewhere (quick/bulk assign) → refresh quietly
  useEffect(() => {
    if (firstLoadRef.current) { firstLoadRef.current = false; return; }
    if (selectedTech) fetchRoute(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const persist = async (orderedStops) => {
    setSaving(true);
    try {
      await axios.put(`${API}/jobs/route-order`, {
        technician_id: selectedTech,
        date: dateStr,
        job_ids: orderedStops.map((s) => s.job_id),
      }, auth());
      await fetchRoute(true);
      toast.success("Stop order saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save order");
      fetchRoute(true);
    } finally {
      setSaving(false);
    }
  };

  const move = (from, to) => {
    if (to < 0 || to >= stops.length || from === to || saving) return;
    const next = arrayMove(stops, from, to);
    setStops(next);
    persist(next);
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    move(stops.findIndex((s) => s.job_id === active.id), stops.findIndex((s) => s.job_id === over.id));
  };

  const resetOrder = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await axios.put(`${API}/jobs/route-order`, { technician_id: selectedTech, date: dateStr, job_ids: [] }, auth());
      await fetchRoute(true);
      toast.success("Back to scheduled-time order");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reset order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-3 space-y-2.5" data-testid="jobs-map-route-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs font-semibold text-foreground">Route planner</span>
        <Select value={selectedTech || ""} onValueChange={setSelectedTech}>
          <SelectTrigger className="h-8 w-[180px] text-xs input-glass" data-testid="route-tech-select">
            <SelectValue placeholder="Choose technician" />
          </SelectTrigger>
          <SelectContent>
            {techs.map((t) => {
              const id = t.technician_id || t.member_id;
              return (
                <SelectItem key={id} value={id} data-testid={`route-tech-option-${id}`}>
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: techColorMap[id] }} />
                    {t.name}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-muted-foreground">{format(date, "EEE, MMM d")}</span>
        <div className="flex items-center gap-2 ml-auto">
          {(loading || saving) && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" data-testid="route-panel-loading" />}
          {route && (
            <span className="text-[11px] text-muted-foreground" data-testid="route-panel-totals">
              {formatDistance(route.total_distance_m)} · {formatDuration(route.total_duration_s)} total
            </span>
          )}
          {selectedTech && orderSource === "manual" && (
            <button
              onClick={resetOrder}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground underline"
              data-testid="route-reset-order"
            >
              <RotateCcw className="w-3 h-3" /> Time order
            </button>
          )}
        </div>
      </div>

      {!selectedTech ? (
        <p className="text-xs text-muted-foreground" data-testid="route-panel-hint">
          Choose a technician to see and arrange their stops for this day. Drag stops (or use the arrows) to set the driving sequence — appointment times stay unchanged.
        </p>
      ) : loading ? null : stops.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="route-panel-empty">
          No mappable jobs for this technician on {format(date, "MMM d")}.
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={stops.map((s) => s.job_id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5" data-testid="route-stop-list">
              {stops.map((s, i) => (
                <StopRow
                  key={s.job_id}
                  stop={s}
                  index={i}
                  count={stops.length}
                  legFromPrev={route?.legs?.[i - 1]}
                  color={color}
                  onMove={move}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {selectedTech && unmapped.length > 0 && (
        <p className="text-[11px] text-amber-500 flex items-center gap-1" data-testid="route-panel-unmapped">
          <AlertTriangle className="w-3 h-3" /> {unmapped.length} job{unmapped.length !== 1 ? "s" : ""} without a locatable address aren't in the route
        </p>
      )}
    </div>
  );
};

export default RoutePanel;
